import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import { Link, useNavigate, useLocation, useNavigationType } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "@/utils/useApi";
import apis from "@/utils/apis";
import { useTranslation } from "react-i18next";
import { buildCdnURL, formatPrice } from "@/utils/utils";
import PullToRefresh from "@/components/PullToRefresh";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

const OrdersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const contentRef = useRef();
  const queryClient = useQueryClient();
  const { callApi: fetchOrders } = useApi();

  // Store initial navigation state - this should not change during component lifecycle
  // Check if user navigated here via browser back/forward button (POP) or clicking link (PUSH)
  const isBackNavigationRef = useRef(navigationType === 'POP');
  const isBackNavigation = isBackNavigationRef.current;

  // On direct visits, reset queries to start fresh from page 1
  useEffect(() => {
    if (!isBackNavigation) {
      queryClient.resetQueries({ queryKey: ['orders'] });
    }
  }, []); // Only run once on mount

  const OrderStatus = {
    PENDING: "pending",
    PROCESSING: "processing",
    SHIPPED: "shipped",
    DELIVERED: "delivered",
    CANCELLED: "cancelled",
  };

  const OrderStatusIcon = {
    [OrderStatus.PENDING]: AlertCircle,
    [OrderStatus.PROCESSING]: Package,
    [OrderStatus.SHIPPED]: Truck,
    [OrderStatus.DELIVERED]: CheckCircle,
    [OrderStatus.CANCELLED]: XCircle,
  };

  const OrderStatusColor = {
    [OrderStatus.PENDING]: "bg-yellow-50 text-yellow-700 border-yellow-200",
    [OrderStatus.PROCESSING]: "bg-blue-50 text-blue-700 border-blue-200",
    [OrderStatus.SHIPPED]: "bg-purple-50 text-purple-700 border-purple-200",
    [OrderStatus.DELIVERED]: "bg-green-50 text-green-700 border-green-200",
    [OrderStatus.CANCELLED]: "bg-red-50 text-red-700 border-red-200",
  };

  // Infinite query for orders
  const {
    data: ordersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ['orders'],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await fetchOrders({
        api: apis.getUserOrders,
        param: `?page=${pageParam}`,
      });
      return {
        orders: res?.orders || [],
        totalPages: res?.totalPages || 1,
        currentPage: pageParam,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    // Use cache only on back navigation, always fetch fresh on direct visits
    staleTime: isBackNavigation ? Infinity : 0,
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Don't refetch - we reset the query on direct visits
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const orders = ordersData?.pages.flatMap(page => page.orders) || [];

  const handleScroll = useCallback(() => {
    setShowScrollTop(window.scrollY > 300);
  }, []);

  const handleRefresh = useCallback(async () => {
    await queryClient.resetQueries({ queryKey: ['orders'] });
  }, [queryClient]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Infinite scroll
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const sentinelRef = useInfiniteScroll(handleLoadMore, hasNextPage, isFetchingNextPage);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusText = (order) => {
    const date = formatDate(order.updatedAt || order.createdAt);
    switch (order.orderStatus) {
      case OrderStatus.DELIVERED:
        return `${t("orders.deliveredOn")} ${date}`;
      case OrderStatus.SHIPPED:
        return `${t("orders.shippedOn")} ${date}`;
      case OrderStatus.CANCELLED:
        return `${t("orders.cancelledOn")} ${date}`;
      case OrderStatus.PROCESSING:
        return `${t("orders.processingSince")} ${date}`;
      case OrderStatus.PENDING:
      default:
        return `${t("orders.orderedOn")} ${date}`;
    }
  };

  return (
    <div className="font-sans">
      <header className="bg-white shadow-sm sticky top-0 z-50 section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 transition-colors dark:text-foreground-100"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground-100 ml-4">
            {t("orders.title")}
          </h1>
        </div>
      </header>

      <PullToRefresh containerRef={contentRef} onRefresh={handleRefresh}>
        <main className="p-3" ref={contentRef}>
          {/* Hide cached orders when showing loading skeleton on direct visit */}
          {!(isLoading || (isRefetching && !isBackNavigation)) && (
            orders?.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-lg shadow-md p-8 text-center"
              >
                <Package className="w-20 h-20 text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold mb-2">
                  {t("orders.no_orders_found.title")}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t("orders.no_orders_found.description")}
                </p>
                <Link
                  to="/"
                  className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors inline-block"
                >
                  {t("orders.no_orders_found.cta")}
                </Link>
              </motion.div>
            ) : (
              <AnimatePresence>
                {orders?.map((order) => (
                <motion.div
                  key={order._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white dark:bg-gray-800 section rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-4"
                >
                  {/* Order Header */}
                  <div className="px-6 py-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                          {getStatusText(order)}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t("orders.orderPrefix")}
                          {order.orderId}
                        </p>
                      </div>
                      <div
                        className={`flex items-center px-3 py-1.5 rounded-full border ${
                          OrderStatusColor[order.orderStatus]
                        }`}
                      >
                        {React.createElement(
                          OrderStatusIcon[order.orderStatus],
                          {
                            className: "w-4 h-4 mr-1.5",
                          }
                        )}
                        <span className="text-sm font-medium">
                          {t(`orders.status.${order.orderStatus}`)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Clickable Order Content */}
                  <Link 
                    to={`/orders/${order._id}`} 
                    className="block"
                    state={{ fromOrdersList: true }}
                  >
                    <div className="px-6 pb-6 transition-colors">
                      <div className="flex items-center justify-between flex-shrink">
                        <div className="flex-1 flex-shrink w-full">
                          <div className="space-y-4 mb-6">
                            {order.products.slice(0, 2).map((item) => (
                              <div
                                key={item._id}
                                className="flex items-center space-x-4"
                              >
                                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden flex-shrink-0">
                                  <img
                                    src={
                                      buildCdnURL(item.product.images[0]) ||
                                      "/assets/placeholder.webp"
                                    }
                                    alt={item.product.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                    {item.product.name}
                                  </h4>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                      {t("orders.orderDetails.qty")}{" "}
                                      {item.quantity}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      •
                                    </span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      ₹{formatPrice(item.price)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {order.products.length > 2 && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                +{order.products.length - 2}{" "}
                                {t("orders.moreItems")}
                              </div>
                            )}
                          </div>

                          {/* Order Summary */}
                          <div className="pt-4 border-t border-gray-100 dark:border-gray-600">
                            <div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t("orders.totalAmount")}
                              </p>
                              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                ₹{formatPrice(order.totalAmount)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Right Chevron Icon */}
                        <div className="ml-4 flex-shrink-0">
                          <ChevronRight className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
            )
          )}

          {/* Skeleton loaders for initial load or refetch on direct visit */}
          {(isLoading || (isRefetching && !isBackNavigation)) && (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 section rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden p-6 animate-pulse"
                >
                  <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mb-6"></div>
                  <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                  <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          )}

          {/* Skeleton loaders for loading more */}
          {isFetchingNextPage && (
            <div className="space-y-4 mt-4">
              {Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="bg-white dark:bg-gray-800 section rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden p-6 animate-pulse"
                >
                  <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mb-6"></div>
                  <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                  <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          )}

          {/* Sentinel element for infinite scroll */}
          {hasNextPage && !isFetchingNextPage && (
            <div ref={sentinelRef} className="h-4 mt-4" />
          )}

          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-20 right-8 bg-white dark:bg-gray-800 p-2 rounded-full shadow-lg transition-opacity duration-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <ChevronUp className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </main>
      </PullToRefresh>
    </div>
  );
};

export default OrdersPage;
